import { Section } from '@/components/ui/Section';
import type { McqContent, ScriptElement, Segment, Slide } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions } from '@/store/editor.store';

import { SegmentList } from './SegmentList';

interface Props {
  slide: Slide;
}

/**
 * Editor for the quiz half of `beatMeta`: difficulty, thresholds, per-wrong-
 * option branches (misconceptionId / diagnosis / reteachLevel1 / reteachLevel2),
 * and the catch-all `generalReteach`.
 *
 * The list of options is sourced from the slide's single mcq element (Rule 4:
 * one quiz slide = one question), falling back to existing `branches` keys
 * if no mcq is present (defensive — pre-save validation will flag that).
 */
export function QuizBranchEditor({ slide }: Props) {
  const actions = useEditorActions();
  const bm = slide.beatMeta;
  if (!bm) return null;

  const mcq = findSlideMcq(slide);
  const mcqContent = (mcq?.content ?? null) as McqContent | null;

  const optionIds = mcqContent?.optionIds ?? Object.keys(bm.branches ?? {});
  const optionLabels = mcqContent?.options ?? [];
  const correctOptionId = (bm.correctOptionId ?? mcqContent?.correctOptionId) ?? null;

  const wrongOptions = optionIds.filter((id) => id !== correctOptionId);
  const branches = (bm.branches ?? {}) as Record<string, {
    misconceptionId?: string | null;
    diagnosis?: string;
    reteachLevel1?: Segment[];
    reteachLevel2?: Segment[];
  }>;

  const difficulty = (bm.difficulty as 'easy' | 'medium' | 'hard' | undefined) ?? 'medium';
  const thresholds = bm.thresholds ?? {};

  return (
    <>
      <Section title="Quiz settings">
        <div className="space-y-2.5">
          <Field label="Difficulty">
            <select
              value={difficulty}
              onChange={(e) =>
                actions.updateBeatMeta(slide.id, {
                  difficulty: e.target.value as 'easy' | 'medium' | 'hard',
                })
              }
              className={cn(
                'h-8 w-full rounded-md border border-border bg-white px-2 text-[12px] text-fg',
                'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20',
              )}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Advance ≥ %">
              <NumberInput
                value={thresholds.advanceIfCorrectPctAbove ?? 70}
                onChange={(v) =>
                  actions.updateBeatMeta(slide.id, {
                    thresholds: { ...thresholds, advanceIfCorrectPctAbove: v },
                  })
                }
                min={0}
                max={100}
              />
            </Field>
            <Field label="Reteach ≥ %">
              <NumberInput
                value={thresholds.reteachIfWrongOptionPctAbove ?? 40}
                onChange={(v) =>
                  actions.updateBeatMeta(slide.id, {
                    thresholds: { ...thresholds, reteachIfWrongOptionPctAbove: v },
                  })
                }
                min={0}
                max={100}
              />
            </Field>
          </div>

          <Field label="Correct option">
            <div className="flex h-8 items-center gap-2 rounded-md border border-border bg-bg-subtle/40 px-2 text-[12px] text-fg-muted">
              {correctOptionId ? (
                <>
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-[10px] font-semibold text-emerald-700">
                    {correctOptionId}
                  </span>
                  <span className="truncate text-fg">
                    {optionLabels[optionIds.indexOf(correctOptionId)] ?? '(option text)'}
                  </span>
                </>
              ) : (
                <span className="text-fg-subtle">Pick the correct answer in the MCQ element →</span>
              )}
            </div>
          </Field>
        </div>
      </Section>

      <Section title={`Wrong-answer branches (${wrongOptions.length})`}>
        {wrongOptions.length === 0 && (
          <p className="rounded-md border border-dashed border-border bg-bg-subtle/40 px-3 py-2 text-[11.5px] text-fg-subtle">
            No wrong options to brand — add MCQ options first.
          </p>
        )}

        {wrongOptions.map((optionId) => {
          const branch = branches[optionId] ?? {};
          const label = optionLabels[optionIds.indexOf(optionId)] ?? '(option text)';
          return (
            <BranchEditor
              key={optionId}
              optionId={optionId}
              optionLabel={label}
              branch={branch}
              onPatch={(patch) => actions.updateQuizBranch(slide.id, optionId, patch)}
            />
          );
        })}
      </Section>

      <Section title="General reteach">
        <p className="-mt-1 mb-2 text-[11px] text-fg-subtle">
          Fallback played when no branch matches (used when thresholds trigger
          a class-wide reteach).
        </p>
        <SegmentList
          segments={(bm.generalReteach ?? []) as Segment[]}
          onChange={(next) => actions.updateBeatMeta(slide.id, { generalReteach: next })}
          emptyLabel="No general reteach segments."
        />
      </Section>
    </>
  );
}

function BranchEditor({
  optionId,
  optionLabel,
  branch,
  onPatch,
}: {
  optionId: string;
  optionLabel: string;
  branch: {
    misconceptionId?: string | null;
    diagnosis?: string;
    reteachLevel1?: Segment[];
    reteachLevel2?: Segment[];
  };
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  return (
    <div className="mb-3 rounded-md border border-border bg-bg-subtle/30 p-2.5 last:mb-0">
      <div className="mb-2 flex items-center gap-2">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-red-100 text-[10px] font-semibold text-red-700">
          {optionId}
        </span>
        <span className="flex-1 truncate text-[12px] font-semibold text-fg">
          {optionLabel}
        </span>
      </div>

      <Field label="Misconception ID (optional)">
        <input
          value={branch.misconceptionId ?? ''}
          onChange={(e) => onPatch({ misconceptionId: e.target.value || null })}
          placeholder="e.g. m1"
          className="h-7 w-full rounded border border-border bg-white px-1.5 text-[11.5px] text-fg focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
        />
      </Field>

      <div className="mt-2">
        <Field label="Diagnosis">
          <textarea
            value={branch.diagnosis ?? ''}
            onChange={(e) => onPatch({ diagnosis: e.target.value })}
            placeholder="Why a student picked this — drives the reteach framing."
            rows={2}
            className="w-full resize-y rounded border border-border bg-white px-2 py-1.5 text-[11.5px] text-fg focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
          />
        </Field>
      </div>

      <div className="mt-2.5">
        <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fg-muted">
          Reteach · Level 1
        </div>
        <SegmentList
          segments={branch.reteachLevel1 ?? []}
          onChange={(next) => onPatch({ reteachLevel1: next })}
          emptyLabel="No level-1 reteach yet."
        />
      </div>

      <div className="mt-2.5">
        <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fg-muted">
          Reteach · Level 2
        </div>
        <SegmentList
          segments={branch.reteachLevel2 ?? []}
          onChange={(next) => onPatch({ reteachLevel2: next })}
          emptyLabel="No level-2 reteach yet."
        />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fg-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(n);
      }}
      className={cn(
        'h-8 w-full rounded-md border border-border bg-white px-2 text-[12px] text-fg',
        'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20',
      )}
    />
  );
}

/** Find the (single) mcq element on a quiz slide. Returns null if none. */
function findSlideMcq(slide: Slide): ScriptElement | null {
  for (const row of slide.rows) {
    for (const col of row.columns) {
      for (const el of col.elements) {
        if (el.type === 'mcq') return el;
      }
    }
  }
  return null;
}
